import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import ColorFilter from '../../../Component/ColorFilter';
import Renderable from '../../../Component/Renderable';
import ButtonComponent from '../../../Component/UI/ButtonComponent';
import {
    StartAnimation,
    StopAnimation,
} from '../../../System/Animation/AnimationHelper';
import Interactable from '../../../Component/Interactable';
import UIItem, { ITextData, IImageData } from '../UIItem';
import Point from '../../../Utility/Point';
import RelativePositionComponent from '../../../Component/Render/RelativePosition';
import TransformComponent from '../../../Component/Transform';
import Game from '../../../Game';
import { AudioTrackType } from '../../../System/Audio/AudioClip';
import { PlaySound } from '../../../System/Audio/AudioHelper';
import Signal from 'Utility/Signal';

/**
 * @param name - Name of button
 * @param text - ITextData for text element
 * @param background - IImageData for background element
 * @param button - IImageData for button element
 */
export interface IRelativeButtonData {
    name: string;
    cacheKey?: string;
    text?: ITextData;
    background?: IImageData;
    button?: IImageData;
    relativePosition?: Point;
    [key: string]: any;
}

export default class RelativeButton extends GameObject {
    protected backgroundParams: IImageData;
    protected buttonParams: IImageData;
    protected textParams: ITextData;
    public readonly onSelected: Signal = new Signal();

    public onCreate(params: IRelativeButtonData): void {
        this.addComponent<TransformComponent>(TransformComponent, params);
        this.addComponent<Renderable>(Renderable, params);
        this.addComponent<ColorFilter>(ColorFilter, params);
        this.addComponent<Interactable>(Interactable, {
            enabled: true,
            buttonMode: true,
            ...params,
        });
        this.addComponent<ButtonComponent>(ButtonComponent, params);

        if (params.relativePosition) {
            this.addComponent(RelativePositionComponent, params);
        }

        if (params.background) {
            this.backgroundParams = params.background;
            const background: GameObject<PIXI.Container> = UIItem.createImage(
                params.background
            );
            this.addChild(background);
        }

        if (params.button) {
            this.buttonParams = params.button;
            const button: GameObject<PIXI.Container> = UIItem.createImage(
                this.buttonParams
            );
            if (this.background) {
                this.background.addChild(button);
            } else {
                this.addChild(button);
            }
        }

        if (params.text) {
            this.textParams = params.text;
            const text: GameObject<PIXI.Container> = UIItem.createText(
                this.textParams
            );
            if (this.button) {
                this.button.addChild(text);
            } else if (this.background) {
                this.background.addChild(text);
            } else {
                this.addChild(text);
            }
        }
        this.enabled = this.getComponent(Interactable).enabled;
    }

    protected selected(): void {
        const buttonComponent: ButtonComponent = this.getComponent(
            ButtonComponent
        );
        if (buttonComponent.onClickAnimation && this.button) {
            StopAnimation(this.button);
            StartAnimation(this.button, buttonComponent.onClickAnimation, {
                scale: new Point(0.95, 0.95),
            });
        } else {
            console.log(this.button);
        }
        // Play Audio
        if (buttonComponent.onClickAudio) {
            PlaySound(buttonComponent.onClickAudio);
        }

        this.onSelected.dispatch();
    }
    protected clicked(): void {
        const buttonComponent: ButtonComponent = this.getComponent(
            ButtonComponent
        );

        if (buttonComponent.onClick) {
            buttonComponent.onClick();
        }
    }
    protected released(): void {
        const buttonComponent: ButtonComponent = this.getComponent(
            ButtonComponent
        );
        if (buttonComponent.onReleaseAnimation) {
            StopAnimation(this.button);
            StartAnimation(this.button, buttonComponent.onReleaseAnimation, {
                scale: new Point(1, 1),
            });
        }
    }

    public get background(): GameObject {
        if (this.backgroundParams)
            return this.findChildByName(this.backgroundParams.name);

        return undefined;
    }
    public get button(): GameObject {
        if (this.buttonParams) {
            return this.findChildByName(this.buttonParams.name);
        }
        return undefined;
    }
    public get text(): GameObject {
        if (this.textParams) {
            return this.findChildByName(this.textParams.name);
        }

        return undefined;
    }
    public set enabled(value: boolean) {
        if (value) {
            this.on(GameObjectEvent.POINTER_DOWN, this.selected.bind(this));
            this.on(GameObjectEvent.POINTER_UP, this.released.bind(this));
            this.on(GameObjectEvent.POINTER_OUT, this.released.bind(this));
            this.on(GameObjectEvent.SELECTED, this.clicked.bind(this));
        } else {
            this.off(GameObjectEvent.POINTER_DOWN);
            this.off(GameObjectEvent.POINTER_UP);
            this.off(GameObjectEvent.POINTER_OUT);
            this.off(GameObjectEvent.SELECTED);
        }
        this.getComponent(Interactable).buttonMode = value;
    }
}
